import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Tooltip } from 'antd';

export type ChartLegendOverflowItem = {
    key: string;
    label: string;
    color: string;
};

const ITEM_GAP_PX = 16;
const LABEL_MAX_CLASS = 'max-w-[140px]';

/** Extra slack so canvas estimates / sub-pixel layout never spill past `overflow-hidden`. */
const WIDTH_SAFETY_PX = 6;

function measureTextWidthPx(text: string, font: string): number {
    if (typeof document === 'undefined') return text.length * 8;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return text.length * 8;
    ctx.font = font;
    return Math.ceil(ctx.measureText(text).width);
}

function estimateMoreButtonWidthPx(hidden: number): number {
    const label = `+${hidden} More`;
    const textW = measureTextWidthPx(label, '600 13px "Open Sans", sans-serif');
    return Math.ceil(textW) + 28 + WIDTH_SAFETY_PX;
}

export type ChartLegendOverflowProps = {
    items: ChartLegendOverflowItem[];
    className?: string;
    'data-test-id'?: string;
};

function LegendChip({
    item,
    mode,
}: {
    item: ChartLegendOverflowItem;
    mode: 'truncate' | 'full';
}) {
    const labelClass =
        mode === 'truncate'
            ? `${LABEL_MAX_CLASS} cursor-default truncate`
            : '';

    return (
        <div className="flex min-w-0 shrink-0 items-center gap-2">
            <div
                className="h-3 w-3 shrink-0 rounded-[3px]"
                style={{ backgroundColor: item.color }}
            />
            <Tooltip title={item.label}>
                <span
                    className={`font-['Open_Sans'] text-[14px] font-normal leading-[22px] text-[rgba(0,0,0,0.65)] ${labelClass}`}
                >
                    {item.label}
                </span>
            </Tooltip>
        </div>
    );
}

/**
 * Single-row chart legend with ellipsis on long labels and a "+N More" overflow menu when the row does not fit.
 */
const ChartLegendOverflow: React.FC<ChartLegendOverflowProps> = ({
    items,
    className = '',
    'data-test-id': dataTestId,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRowRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(() => (items.length <= 1 ? items.length : 1));

    const itemsSignature = useMemo(() => items.map((i) => `${i.key}:${i.label}`).join('|'), [items]);

    useLayoutEffect(() => {
        if (!items.length) {
            setVisibleCount(0);
            return;
        }

        const measure = () => {
            const measureEl = measureRowRef.current;
            const containerEl = containerRef.current;
            if (!measureEl || measureEl.children.length !== items.length) return;

            const itemWidths = Array.from(measureEl.children).map((child) =>
                Math.ceil(child.getBoundingClientRect().width),
            );

            const containerW = (containerEl?.clientWidth ?? 0) - WIDTH_SAFETY_PX;

            // Until the row has a real width, avoid rendering all chips (would clip). Prefer one chip + "+more".
            if (containerW <= 0) {
                setVisibleCount(items.length <= 1 ? items.length : 1);
                return;
            }

            let best = 0;
            for (let k = items.length; k >= 0; k--) {
                const hidden = items.length - k;
                let w = 0;
                for (let i = 0; i < k; i++) {
                    w += itemWidths[i] ?? 0;
                    if (i < k - 1) w += ITEM_GAP_PX;
                }
                if (hidden > 0) {
                    if (k > 0) w += ITEM_GAP_PX;
                    w += estimateMoreButtonWidthPx(hidden);
                }
                if (w <= containerW) {
                    best = k;
                    break;
                }
            }
            setVisibleCount(best);
        };

        measure();

        const el = containerRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;

        const ro = new ResizeObserver(() => measure());
        ro.observe(el);
        return () => ro.disconnect();
    }, [items, items.length, itemsSignature]);

    if (!items.length) return null;

    const visible = items.slice(0, visibleCount);
    const overflow = items.slice(visibleCount);
    const showMore = overflow.length > 0;

    return (
        <div className={`relative w-full min-w-0 ${className}`} data-test-id={dataTestId}>
            {/* Hidden row: same chips as shown — DOM widths drive how many fit (canvas estimates were unreliable). */}
            <div
                ref={measureRowRef}
                className="pointer-events-none invisible absolute left-0 top-0 z-[-1] flex min-w-max flex-nowrap gap-4"
                aria-hidden
            >
                {items.map((item) => (
                    <LegendChip key={item.key} item={item} mode="truncate" />
                ))}
            </div>

            <div
                ref={containerRef}
                className="flex w-full min-w-0 flex-nowrap items-center justify-start gap-4 overflow-hidden"
            >
                {visible.map((item) => (
                    <LegendChip key={item.key} item={item} mode="truncate" />
                ))}

                {showMore && (
                    <Dropdown
                        trigger={['click']}
                        placement="bottomRight"
                        dropdownRender={() => (
                            <div className="min-w-[220px] rounded-lg bg-white py-2 shadow-[0px_8px_24px_rgba(0,0,0,0.12)]">
                                {overflow.map((item) => (
                                    <div
                                        key={item.key}
                                        className="flex cursor-default items-center gap-2 px-3 py-2 hover:bg-[#F0EBFF]"
                                    >
                                        <div
                                            className="h-3 w-3 shrink-0 rounded-[3px]"
                                            style={{ backgroundColor: item.color }}
                                        />
                                        <span className="font-['Open_Sans'] text-[14px] font-normal leading-[22px] text-[rgba(0,0,0,0.65)]">
                                            {item.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    >
                        <button
                            type="button"
                            className="shrink-0 cursor-pointer rounded-full border-0 bg-[#F0EBFF] px-3 py-1 font-['Open_Sans'] text-[13px] font-semibold leading-[20px] text-[#5B50D1] outline-none transition-opacity hover:opacity-90"
                            aria-label={`${overflow.length} more legend entries`}
                        >
                            +{overflow.length} More
                        </button>
                    </Dropdown>
                )}
            </div>
        </div>
    );
};

export default ChartLegendOverflow;
